import { useEffect } from "react";
import { useSiteSettings } from "@/lib/settings";

function upsertMeta(attr, key, content) {
  if (content === undefined || content === null || content === "") return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

export function useSEO({
  title,
  description,
  image,
  type = "website",
  noIndex = false,
  keywords,
  pageKey,
  jsonLd,
  jsonLdId = "page-jsonld",
  breadcrumbs,
}) {
  const { settings } = useSiteSettings();
  const brand = `${settings.brand_left || "Pharmaco"}${settings.brand_right || "Hires"}`;
  const seo = settings.seo || {};
  const pageOverride = (pageKey && settings.page_seo?.[pageKey]) || null;

  useEffect(() => {
    // Priority: explicit prop > per-page admin override > global default
    const effectiveTitle = pageOverride?.title || title;
    const fullTitle = effectiveTitle
      ? (effectiveTitle.includes(brand) ? effectiveTitle : `${effectiveTitle} · ${brand}`)
      : (seo.default_title || `${brand} — ${settings.tagline || ""}`);
    const url = typeof window !== "undefined" ? window.location.href.split("#")[0] : "";
    const desc = pageOverride?.description || description || seo.default_description;
    const kw = pageOverride?.keywords || keywords || seo.keywords;
    const ogImage = pageOverride?.og_image || image || seo.og_image || settings.logo_url;
    const effectiveNoIndex = noIndex || !!pageOverride?.no_index;

    document.title = fullTitle;
    if (desc) upsertMeta("name", "description", desc);
    if (kw) upsertMeta("name", "keywords", kw);
    upsertMeta("name", "robots", effectiveNoIndex ? "noindex,nofollow" : "index,follow");
    upsertMeta("name", "author", brand);

    // Google Search Console verification
    if (seo.google_site_verification) {
      upsertMeta("name", "google-site-verification", seo.google_site_verification);
    }

    // Open Graph
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:site_name", brand);
    upsertMeta("property", "og:locale", "en_IN");
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", type);
    if (desc) upsertMeta("property", "og:description", desc);
    if (ogImage) upsertMeta("property", "og:image", ogImage);

    // Twitter
    upsertMeta("name", "twitter:card", ogImage ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:title", fullTitle);
    if (desc) upsertMeta("name", "twitter:description", desc);
    if (ogImage) upsertMeta("name", "twitter:image", ogImage);
    if (seo.twitter_handle) upsertMeta("name", "twitter:site", seo.twitter_handle);

    // Canonical
    upsertLink("canonical", url);

    if (jsonLd) upsertJsonLd(jsonLdId, jsonLd);
    else removeJsonLd(jsonLdId);

    if (breadcrumbs && breadcrumbs.length) {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      upsertJsonLd("page-breadcrumbs-jsonld", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: b.url ? `${origin}${b.url}` : undefined,
        })),
      });
    } else {
      removeJsonLd("page-breadcrumbs-jsonld");
    }

    return () => {
      removeJsonLd(jsonLdId);
      removeJsonLd("page-breadcrumbs-jsonld");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, image, type, noIndex, keywords, pageKey, brand, JSON.stringify(seo), JSON.stringify(pageOverride), settings.logo_url, JSON.stringify(jsonLd || null), JSON.stringify(breadcrumbs || null)]);
}
